/**
 * Lightweight helpers for the in-app "new version available" prompt.
 *
 * The app's CURRENT version is read from package.json at build-time. The
 * backend exposes the LATEST version via GET /api/public/app-version, and
 * the admin can change it via PUT /api/admin/app-version.
 */
import pkg from "../../package.json";

export const APP_VERSION = pkg.version || "1.0.0";

/**
 * Returns -1 if a < b, 0 if equal, 1 if a > b. Tolerant of "1.2", "v1.2.3"
 * and pre-release suffixes (which are stripped). Non-numeric segments are
 * treated as 0.
 */
export function compareVersions(a, b) {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const norm = (v) => String(v).replace(/^v/i, "").split("-")[0].split(".").map((x) => parseInt(x, 10) || 0);
  const A = norm(a), B = norm(b);
  const len = Math.max(A.length, B.length);
  for (let i = 0; i < len; i++) {
    const ai = A[i] || 0, bi = B[i] || 0;
    if (ai !== bi) return ai < bi ? -1 : 1;
  }
  return 0;
}

/** Returns one of: "android", "ios", "web". Capacitor-aware. */
export function getPlatform() {
  if (typeof window === "undefined") return "web";
  const cap = window.Capacitor;
  if (cap && typeof cap.getPlatform === "function") {
    const p = cap.getPlatform();
    if (p === "android" || p === "ios") return p;
  }
  // Best-effort UA sniff for the iOS PWA case (Capacitor not always defined)
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  return "web";
}

export function isNativePlatform() {
  const p = getPlatform();
  return p === "android" || p === "ios";
}

/**
 * Performs the actual "update" action.
 *  - Android Capacitor → open the APK download URL in the system browser.
 *  - iOS / PWA / web   → unregister SW + delete caches + hard reload (to
 *                        pick up new bundle from web_url).
 * Returns void; never rejects.
 */
export async function performUpdate({ android_apk_url, ios_url, web_url } = {}) {
  const platform = getPlatform();
  try {
    if (platform === "android" && android_apk_url) {
      try {
        const Browser = window.Capacitor?.Plugins?.Browser;
        if (Browser?.open) { await Browser.open({ url: android_apk_url }); return; }
      } catch (_) { /* fall through */ }
      window.open(android_apk_url, "_system");
      return;
    }
    // Best-effort PWA cache wipe so the new bundle is fetched.
    await clearWebAppCaches();
    const target = (platform === "ios" && ios_url) ? ios_url : (web_url || window.location.origin);
    // Cache-bust the navigation
    const sep = target.includes("?") ? "&" : "?";
    window.location.replace(`${target}${sep}_v=${Date.now()}`);
  } catch (e) {
    // Last resort: reload current page
    try { window.location.reload(); } catch (_) { /* noop */ }
  }
}

/**
 * Unregisters service workers and deletes all Cache Storage entries. Used both
 * by the update prompt's "Atualizar agora" button and exposed as a manual
 * action in the admin/app/diagnostic tools.
 */
export async function clearWebAppCaches() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (_) { /* noop */ }
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (_) { /* noop */ }
}
